
import { LzEncryptor } from "../../utils";
import { NoseurObject } from "@rivtn/noseur";
import { SessionStorageCacheManager } from "kyofuuc";
import { BaseService, IFactorResponse, IGallery, OmneHostPlan, OmneHostStatus, SanitizedResponse, Visibility, IWebsitePage } from "../BaseService";

export interface IMember {
    username: string;
    tag_line: string;
    plan: OmneHostPlan;
    external_id: string;
    platform_id: string;
    country_code: string;
    status: OmneHostStatus;
    profile_banner_url: string;
    profile_picture_url: string;
    extra_data: NoseurObject<any>;
    janus_user_external_id: string;
    social_accounts: NoseurObject<string>;
    user_detail: {
        email: string;
        phone: string;
        user_type: string;
        last_name: string;
        first_name: string;
    }
}

export interface IInterestsAndAi {
    interests: string[];
    external_id: string;
    use_data_to_train_ai: boolean;
    use_interest_to_guide_ai: boolean;
    ai_generation_prompts: { id: string; value: string; }[];
}

export interface IPaymentMethod {
    type: string;
    currency: string;
    is_default: boolean;
    external_id: string;
    processor_id: string;
    details: NoseurObject<any>;
}

export interface IBillingHistory {
    plan: string;
    status: string;
    amount: number;
    currency: string;
    created_at: string;
    external_id: string;
    details: NoseurObject<any>;
    type: "UPGRADE" | "DOWNGRADE";
    duration: "MONTHLY" | "YEARLY";
    payment_method: NoseurObject<any>;
    receipt_download_location?: string;
}

export interface IProfileWebsite {
    created_at: string;
    sponsors: string[];
    external_id: string;
    display_name: string,
    profile_detail: string;
    visibility: Visibility;
    extra_data: NoseurObject<any>;
    website_pages: {
        HOME: IWebsitePage;
        MERCH: IWebsitePage;
        GALLERY: IWebsitePage;
        SCHEDULE: IWebsitePage;
        ACHIEVEMENTS: IWebsitePage;
        TEAM_HISTORY: IWebsitePage;
    };
    media: {
        LOGO: string[];
        BANNER: string[];
        PASSPORT: string[];
        HALF_BODY: string[];
        SELF_ANIMATION: string[];
    };
    gallery_settings: {
        enable_rivtn_ad: boolean;
    };
    design_settings: {
        design_id: string;
        scheme_settings?: NoseurObject<string>;
    };
    merchandise_settings: {
        storefront_id: boolean;
        enable_rivtn_ad: boolean;
    };
    team_history_settings: {
        enable_rivtn_ad: boolean;
        show_current_teams: boolean;
        show_previous_teams: boolean;
        team_settings: NoseurObject<boolean>;
    };
    home_settings: {
        show_schedules: boolean;
        show_sponsors: boolean;
        enable_rivtn_ad: boolean,
        show_top_gallery: boolean;
        show_twitter_feeds: boolean;
        show_top_activities: boolean;
        show_twitch_streams: boolean;
        show_instagram_feeds: boolean;
    };
    achievement_settings: {
        enable_rivtn_ad: boolean;
        show_trophies_graphs: boolean;
        show_winnings_trails: boolean;
        show_winnings_graphs: boolean;
        show_trophies_trails: boolean;
        show_leaderboard_trails: boolean;
        show_leaderboard_graphs: boolean;
        show_team_trophies_graphs: boolean;
        show_team_trophies_trails: boolean;
        show_team_winnings_trails: boolean;
        show_team_winnings_graphs: boolean;
    };
    schedule_settings: {
        enable_rivtn_ad: boolean;
        show_past_team_matches: boolean;
        show_past_personal_matches: boolean;
        show_ongoing_upcoming_team_matches: boolean;
        show_ongoing_upcoming_personal_matches: boolean;
    };
}

export class MemberService extends BaseService {

    protected static instance: MemberService;

    private constructor() {
        super();
    }

    static getInstance() {
        if (!MemberService.instance) MemberService.instance = new MemberService();
        return MemberService.instance;
    }

    getProfile(refreshCache?: boolean): Promise<any> { // TODO only cache if verified
        return this.report(this.transport.get("/v1/member/profile", { cache: true, key: `PROFILE`, refreshCache, cacheManager: new SessionStorageCacheManager({ encryptKey: true, encryptor: LzEncryptor, warnOnLowSpace: true }) }));
    }

    upsertProfile(payload: NoseurObject<any>): Promise<any> {
        return this.report(this.transport.post("/v1/member/profile", payload), ({ sanitized }: SanitizedResponse<IMember>) => {
            this.getProfile(true).catch(() => { });
        });
    }

    requestAccountVerificationEmail(): Promise<SanitizedResponse<void>> {
        return this.report(this.transport.get(`/v1/member/verify`, { query: { prefix: `${window.location.origin}/verify` } }));
    }

    verifyEmail(payload: NoseurObject<any>): Promise<SanitizedResponse<void>> {
        return this.report(this.transport.post(`/v1/member/verify`, payload));
    }

    getInterestsAndAi(refreshCache?: boolean): Promise<SanitizedResponse<IInterestsAndAi>> {
        return this.report(this.transport.get("/v1/member/interests_ai", { cache: true, key: `INTERESTS_AND_AI`, refreshCache, cacheLifetime: 1000 * 60 * 60, }));
    }

    updateInterestsAndAi(payload: NoseurObject<any>): Promise<SanitizedResponse<IInterestsAndAi>> {
        return this.report(this.transport.post("/v1/member/interests_ai", payload));
    }

    updateSocialAccounts(payload: NoseurObject<any>): Promise<SanitizedResponse<IMember>> {
        return this.report(this.transport.put("/v1/member/socials", payload));
    }

    requestDataDownload(): Promise<SanitizedResponse<void>> {
        return this.report(this.transport.get("/v1/member/data/download"));
    }

    requestDataClearing(): Promise<SanitizedResponse<IFactorResponse>> {
        return this.report(this.transport.get("/v1/member/data/clear"));
    }

    confirmDataClearing(factor: NoseurObject<any>): Promise<SanitizedResponse<void>> {
        return this.report(this.transport.post("/v1/member/data/clear", factor));
    }

    requestDataDeletion(): Promise<SanitizedResponse<IFactorResponse>> {
        return this.report(this.transport.get("/v1/member/data/delete"));
    }

    confirmDataDeletion(factor: NoseurObject<any>): Promise<SanitizedResponse<void>> {
        return this.report(this.transport.post("/v1/member/data/delete", factor));
    }

    // PAYMENT METHODS

    queryPaymentMethods(currencyCode: string, params: NoseurObject<string | number | boolean> = { page: 1, size: 100, sort: "created_at,asc" }): Promise<SanitizedResponse<IPaymentMethod>> {
        return this.report(this.transport.get("/v1/member/payment/method", { query: this.normalizeParams({ ...params, currency__eq: currencyCode }) }));
    }

    addPaymentMethod(payload: NoseurObject<any>): Promise<any> {
        return this.report(this.transport.post("/v1/member/payment/method", {
            ...payload,
            screen_dimension: {
                width: window.outerWidth,
                height: window.outerHeight,
            }
        }));
    }

    changeDefaultPaymentMethod(id: string): Promise<any> {
        return this.report(this.transport.put(`/v1/member/payment/method/${id}/default`, {}));
    }

    deletePaymentMethod(id: string): Promise<any> {
        return this.report(this.transport.delete(`/v1/member/payment/method/${id}`));
    }

    // END PAYMENT METHODS

    // BILLING HISTORY AND PLAN

    queryBillingHistories(currencyCode: string, params: NoseurObject<string | number | boolean | undefined> = { page: 1, size: 100 }): Promise<SanitizedResponse<IBillingHistory>> {
        return this.report(this.transport.get("/v1/member/billing/history", { query: this.normalizeParams({ ...params, currency__eq: currencyCode }) }));
    }

    requestPlanChange(payload: { type: "UPGRADE" | "DOWNGRADE"; plan: string; duration: string; payment_method_id?: string; }): Promise<any> {
        return this.report(this.transport.post("/v1/member/plan/change", payload));
    }

    makePlanChangePayment(payload: { value: string; factor_id: string; }): Promise<any> {
        return this.report(this.transport.patch("/v1/member/plan/change", payload));
    }

    // BILLING HISTORY AND PLAN

    // WEBSITE AND GALLERY

    getProfileWebsite(): Promise<SanitizedResponse<IProfileWebsite>> {
        return this.report(this.transport.get("/v1/member/profile/website"));
    }

    updateProfileWebsite(profileWebsite: IProfileWebsite): Promise<SanitizedResponse<IProfileWebsite>> {
        return this.report(this.transport.patch("/v1/member/profile/website", profileWebsite));
    }

    getProfileWebsiteGallery(): Promise<SanitizedResponse<IGallery>> {
        return this.report(this.transport.get("/v1/member/profile/website/gallery", { query: this.normalizeParams({ size: 100 }) }));
    }

    createProfileWebsiteGallery(gallery: Partial<IGallery>): Promise<any> {
        return this.report(this.transport.post("/v1/member/profile/website/gallery", gallery));
    }

    updateProfileWebsiteGallery(gallery: Partial<IGallery>): Promise<any> {
        return this.report(this.transport.patch(`/v1/member/profile/website/gallery/${gallery.external_id}`, gallery));
    }

    deleteProfileWebsiteGallery(gallery: Partial<IGallery>): Promise<any> {
        return this.report(this.transport.delete(`/v1/member/profile/website/gallery/${gallery.external_id}`));
    }

    // END WEBSITE


}
